import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import config from "./config";
import { AuthUser } from "@/core/auth/models/auth.model";

interface FailedRequests {
  resolve: (value: AxiosResponse) => void;
  reject: (value: AxiosError) => void;
  config: AxiosRequestConfig;
  error: AxiosError;
}

let failedRequests: FailedRequests[] = [];
let isTokenRefreshing = false;

const getStoredAccessToken = () => {
  return localStorage.getItem("accessToken");
};

const getStoredUser = () => {
  return (JSON.parse(localStorage.getItem("user")) as AuthUser) ?? null;
};
const axiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: 60000,
  withCredentials: true,
});
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = getStoredAccessToken();
    const user = getStoredUser();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      config.headers["Content-Type"] = "application/json";
      config.headers["Access-Control-Allow-Credentials"] = true;
    }
    if (user) {
      if (user.role === "company-admin" || user.role === "employee") {
        config.params = { ["companyId"]: user.company._id };
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequestConfig = error.config!;

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (isTokenRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequests.push({
          resolve,
          reject,
          config: originalRequestConfig,
          error: error,
        });
      });
    }

    isTokenRefreshing = true;

    try {
      
      const response = await axiosInstance.post("/auth/refresh-token");

      const { accessToken } = response.data ?? {};

      if (!accessToken) {
        throw new Error(
          "Something went wrong while refreshing your access token"
        );
      }

      localStorage.setItem("accessToken", accessToken);

      failedRequests.forEach(({ resolve, reject, config }) => {
        axiosInstance(config)
          .then((response) => resolve(response))
          .catch((error) => reject(error));
      });
    } catch (_error: unknown) {
      console.log("refresh token error", _error);

      failedRequests.forEach(({ reject, error }) => reject(error));
      localStorage.setItem("accessToken", "");
      window.location.href = "/sign-in";

      return Promise.reject(error);
    } finally {
      failedRequests = [];
      isTokenRefreshing = false;
    }

    return axiosInstance(originalRequestConfig);
  }
);

export { axiosInstance };
